import semver from 'semver';
import { constants as appStorageConstants } from '@saucelabs/sl-module-app-storage';

/**
 * This helper function calculates if a device is considered "locked" and "busy" based on
 * some boolean input values.
 * @param {boolean} activeFreeUser - if the user is in an active free user
 * @param {boolean} expiredUser - if the user account is expired trial or expired subscription
 * @param {boolean} freeOfCharge - if the device is free of charge (available for trial
 *   and freemium users)
 * @param {boolean} disabled - if the device is stated as "disabled" from the backend
 * @return {object} - an object containing the resulting "busy" and "locked" properties
 */
const calculateLockedAndBusyStatesForRealDevice = (
  activeFreeUser,
  expiredUser,
  freeOfCharge,
  disabled,
) => {
  // A device is locked when:
  // 1) the user is free (trial or freemium) and the device is not free of charge
  // 2) the user is in an expired state
  const locked = (activeFreeUser && !freeOfCharge) || expiredUser;

  // A device is considered busy when it is disabled and not locked
  const busy = disabled && !locked;

  return { locked, busy };
};

/**
 * Determines whether an app is compatible with a device
 * @param {AppStorageFile} app
 * @param {DeviceDescriptor (Immutable.Record)} device
 * @returns {boolean}
 */

const isAppStorageFileCompatibleWithRealDeviceDescriptor = (app, device) => {
  if (!(app && device)) {
    return false;
  }

  const appKindAsLowerCase = app.kind.toLowerCase();
  if (appKindAsLowerCase !== device.os?.toLowerCase()) {
    return false;
  }

  if (appKindAsLowerCase === appStorageConstants.APP_STORAGE_KIND.ANDROID) {
    // we assume compatibility if the device api level or app min_sdk is missing or not set
    if (!device.apiLevel || !app.metadata?.min_sdk) {
      return true;
    }

    return app.metadata.min_sdk <= device.apiLevel;
  }

  if (appKindAsLowerCase === appStorageConstants.APP_STORAGE_KIND.IOS) {
    const semverMinOs = semver.coerce(app.metadata?.min_os);
    const semverDeviceOsVersion = semver.coerce(device.osVersion);
    const deviceFamilies = app.metadata?.device_family || [];
    const formFactor = device.formFactor?.toLowerCase();

    if (!deviceFamilies.includes(formFactor)) {
      return false;
    }

    // semver.coerce will return null if it cannot coerce the value
    if (!semverMinOs || !semverDeviceOsVersion) {
      return true;
    }

    return semver.lte(semverMinOs, semverDeviceOsVersion);
  }

  return false;
};

/**
 * Determines whether a device is available
 * @param {DeviceDescriptor (Immutable.Record)} device
 * @param {Array} availableDeviceIds - list of descriptorIds
 * @param {boolean} rdcSessionAvailable - based on RDC concurrency
 * @returns {boolean}
 */
const isDeviceDisabled = (device, availableDeviceIds, rdcSessionAvailable) => (
  !device ||
  !device.includedInPlan ||
  !availableDeviceIds ||
  !availableDeviceIds.includes(device.descriptorId) ||
  (!rdcSessionAvailable && !device.isPrivateDevice())
);

export default {
  calculateLockedAndBusyStatesForRealDevice,
  isAppStorageFileCompatibleWithRealDeviceDescriptor,
  isDeviceDisabled,
};
